import { domIsChildOf } from 'pf-frontend-common/src/helper/dom/is-child-of';
import { domQuerySelectorAll } from 'pf-frontend-common/src/helper/dom/query-selector-all';
import { objectMerge } from 'pf-frontend-common/src/helper/object/merge';
import { BrowserViewport } from 'pf-frontend-common/src/module/browser/viewport';
import { BrowserViewportAwareInterface } from 'pf-frontend-common/src/module/browser/viewport-aware.interface';
import { DataKeyValueStringInterface } from 'pf-frontend-common/src/module/data/key-value/string.interface';
import { DomElementEvent } from 'pf-frontend-common/src/module/dom/element.event';
import { EventEmitterInterface } from 'pf-frontend-common/src/module/event/emitter.interface';
import { StatsContextLocalInterface } from 'pf-frontend-common/src/module/stats/context/local.interface';
import { StatsEntityEnum } from 'pf-frontend-common/src/module/stats/entity.enum';
import { StatsAttributionServiceInterface } from 'pf-frontend-common/src/service/stats-attribution/service.interface';
import { StatsDataServiceInterface } from 'pf-frontend-common/src/service/stats-data/service.interface';
import { WindowLocationServiceInterface } from 'pf-frontend-common/src/service/window-location/service.interface';

import { AgentModel } from 'common/data/agent/model';
import { AgentStatsDataAdapter } from 'common/data/agent/stats/data-adapter';
import { i18nTranslate } from 'common/helper/i18n/translate';
import { AgentSerpStatsPaginationAdapter } from 'common/module/agent-serp/stats/pagination-adapter';
import { AgentSerpStore } from 'common/module/agent-serp/store';
import { DataStore } from 'common/module/data/store';
import { DataStoreEvent } from 'common/module/data/store.event';
import { DataStoreDispatchEventInterface } from 'common/module/data/store/dispatch-event.interface';
import { AgentStoreServiceInterface } from 'common/service/agent-store/service.interface';
import { StatsServiceInterface } from 'common/service/stats/service.interface';
import { AgentCard1TemplatePropsInterface } from 'desktop/module/agent/card-1/template-props.interface';
import { AgentList1TemplatePropsInterface } from 'desktop/module/agent/list-1/template-props.interface';
import { AgentTabListEvent } from 'desktop/module/agent/tab/list.event';

/**
 * @deprecated
 *
 * TODO-FE[WEB-1602]: Remove this in favor of common AgentListViewStore
 */
export class AgentTabListViewStore extends DataStore implements BrowserViewportAwareInterface {
  /**
   * @inheritDoc
   */
  protected state: AgentList1TemplatePropsInterface;

  /**
   * CSS Selectors
   */
  private cssSelector: DataKeyValueStringInterface = {
    cta: '[data-qs="cta"]',
  };

  /**
   * Constructor
   */
  constructor(
    eventEmitter: EventEmitterInterface,
    private agentStoreService: AgentStoreServiceInterface,
    private agentSerpStore: AgentSerpStore,
    private statsService: StatsServiceInterface,
    private statsDataService: StatsDataServiceInterface,
    private statsAttributionService: StatsAttributionServiceInterface,
    private agentStatsDataAdapter: AgentStatsDataAdapter,
    private windowLocationService: WindowLocationServiceInterface,
    private browserViewport: BrowserViewport
  ) {
    super(eventEmitter);
  }

  /**
   * @inheritDoc
   */
  public getBrowserViewport(): BrowserViewport {
    return this.browserViewport;
  }

  /**
   * @inheritDoc
   */
  public initialize(): void {
    this.state = {
      disabled: false,
      noResultsMessage: i18nTranslate('This broker has no agents'),
      agents: [],
    };

    this.refreshState();

    // Attach observers
    this.observers();
  }

  /**
   * @inheritDoc
   */
  public updateViewport(): void {
    // View disabled
    if (this.state.disabled === true) {
      // Don't update the viewport
      return;
    }

    // For each agent
    const iMax = this.state.agents.length;
    for (let i = 0; i < iMax; i++) {
      // Reset watcher (which will check the element's position within the viewport)
      this.browserViewport.resetWatcher(String(i));
    }
  }

  /**
   * Updates in the viewport the HTML element linked to the agent's index within the list
   */
  public updateHtmlElement(index: number, element: HTMLElement): void {
    this.browserViewport.registerWatcher(String(index), element);
  }

  /**
   * @inheritDoc
   */
  public updateDisabled(disabled: boolean): void {
    // Update state
    this.state.disabled = disabled;
    this.setState(this.state, this.updateDisabled);

    // Update viewport
    this.updateViewport();
  }

  /**
   * Attach observers
   */
  protected observers(): void {
    // Agent SERP store
    this.agentSerpStore
      .getEventEmitter()
      .addListener(DataStoreEvent.updateState, this.onUpdateStateAgentSerpStore.bind(this));

    // Browser viewport
    this.browserViewport.getEventEmitter().addListener(DomElementEvent.enterViewport, this.onEnterViewport);
  }

  /**
   * Get stats context local to this view
   */
  protected getStatsContext(): StatsContextLocalInterface {
    return {
      pagination: new AgentSerpStatsPaginationAdapter(this.agentSerpStore.getState()).getData(),
    };
  }

  /**
   * Refresh state
   *
   * @deprecated Stinky "refresh state" keywords detected, most likely a logic somewhere else is wrong
   */
  private refreshState(): void {
    const agentSerpStoreState = this.agentSerpStore.getState();
    const agents: AgentCard1TemplatePropsInterface[] = [];

    // For each result
    agentSerpStoreState.results.list.forEach((agentId, index) => {
      // Retrieve loaded agent model
      const agentModel = this.agentStoreService.getOneById(agentId);

      // No agent loaded
      if (!agentModel) {
        return;
      }

      // Default position without pagination
      let position = index + 1;

      // Pagination params
      if (agentSerpStoreState.params['page[number]'] && agentSerpStoreState.params['page[limit]']) {
        // Add previous records count
        position += (agentSerpStoreState.params['page[number]'] - 1) * agentSerpStoreState.params['page[limit]'];
      }

      // Register stats entity
      this.statsDataService.getAgentStore().add(this.agentStatsDataAdapter.getData(agentModel, position, index + 1));

      // Add agent card
      agents.push({
        agent: agentModel,
        onCreateTemplate: this.onCreateTemplate,
        onClick: this.onClickAgent,
        onClickEmail: this.onClickEmail,
        onClickCall: this.onClickCall,
      });

      // Log stats
      this.statsService.agentLoad(parseInt(agentId, 10), this.getStatsContext());
    });

    // Set state
    this.setState({
      ...this.state,
      agents,
    });
  }

  /**
   * Clicked agent
   */
  private onClickAgent = (e: Event, agentModel: AgentModel): void => {
    const clickedCta = domIsChildOf(
      <HTMLElement>e.target,
      domQuerySelectorAll(<HTMLElement>e.currentTarget, this.cssSelector.cta)
    );

    // Clicked a CTA
    if (clickedCta) {
      return;
    }

    // Log stats
    this.statsService.agentClick(
      parseInt(agentModel.id, 10),
      objectMerge({}, this.getStatsContext(), {
        authentication: {
          attributionId: this.statsAttributionService.push(StatsEntityEnum.agent, agentModel.id),
        },
      })
    );

    // Clicked on link
    if ((<HTMLElement>e.target).tagName === 'A') {
      return;
    }

    // Redirect to agent page
    this.windowLocationService.redirect(agentModel.links.profile, {
      hash: null,
      queryData: {},
    });
  };

  /**
   * Clicked call button
   */
  private onClickCall = (e: Event, agentModel: AgentModel): void => {
    e.preventDefault();
    e.stopPropagation();

    const context: StatsContextLocalInterface = {
      ...this.getStatsContext(),
      lead: {
        medium: 'phone',
        cta: 'button',
      },
    };

    // Log stats
    this.statsService.agentLeadClick(parseInt(agentModel.id, 10), context);
  };

  /**
   * Clicked email button
   */
  private onClickEmail = (e: Event, agentModel: AgentModel): void => {
    e.preventDefault();
    e.stopPropagation();

    const context: StatsContextLocalInterface = {
      ...this.getStatsContext(),
      lead: {
        medium: 'email',
        cta: 'button',
      },
    };

    // Log stats
    this.statsService.agentLeadClick(parseInt(agentModel.id, 10), context);

    this.getEventEmitter().emit(AgentTabListEvent.clickEmail, e, agentModel);
  };

  /**
   * Created template
   */
  private onCreateTemplate = (element: HTMLElement, index: number): void => {
    this.updateHtmlElement(index, element);
  };

  /**
   * Agent serp store state update event handler
   */
  private onUpdateStateAgentSerpStore = (storeDispatchEvent: DataStoreDispatchEventInterface): void => {
    this.updateDisabled(true);

    if (storeDispatchEvent.action === this.agentSerpStore.updateResults) {
      this.refreshState();

      return;
    }
  };

  /**
   * An element entered the browser viewport
   */
  private onEnterViewport = (watcherId: string): void => {
    // View is disabled
    if (this.state.disabled === true) {
      return;
    }

    // Agent card doesn't exist
    if (!this.state.agents[parseInt(watcherId, 10)]) {
      return;
    }

    // Log stats
    this.statsService.agentImpression(
      parseInt(this.state.agents[parseInt(watcherId, 10)].agent.id, 10),
      this.getStatsContext()
    );
  };
}
